import { useEffect } from 'react'
import type { Poll } from '@vc/models/poll'
import {
  useMeetingPollsStore,
  selectActivePoll,
} from '@/features/meeting/store/meeting-polls.store'
import { pollsApi } from '@/features/meetings/services/meetings.api'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'

type PollChannel = {
  subscribe: (event: string, handler: (payload: any) => void) => () => void
} | null

/**
 * Polls for the current meeting.
 *
 * Two sources, one store: a fetch on join so a late arrival sees polls raised
 * before they got here, and the room channel for everything after. Without the
 * fetch, joining mid-poll would show an empty panel until the host happened to
 * do something.
 */
export function usePolls(options?: { meetingId?: string; channel?: PollChannel }) {
  const meetingId = options?.meetingId
  const channel = options?.channel ?? null

  const polls = useMeetingPollsStore((s) => s.polls)
  const activePollId = useMeetingPollsStore((s) => s.activePollId)
  const pollVotes = useMeetingPollsStore((s) => s.pollVotes)
  const authoredPollIds = useMeetingPollsStore((s) => s.authoredPollIds)
  const promptPollId = useMeetingPollsStore((s) => s.promptPollId)
  const activePoll = useMeetingPollsStore(selectActivePoll)

  const setActivePollId = useMeetingPollsStore((s) => s.setActivePollId)
  const handleCreatePoll = useMeetingPollsStore((s) => s.handleCreatePoll)
  const handleVotePoll = useMeetingPollsStore((s) => s.handleVotePoll)
  const handleEndPoll = useMeetingPollsStore((s) => s.handleEndPoll)

  // The store needs the id to write; it has no route context of its own.
  useEffect(() => {
    useMeetingPollsStore.getState().setMeetingId(meetingId ?? null)
  }, [meetingId])

  // Catch up on what already exists.
  useEffect(() => {
    if (!meetingId) return
    let cancelled = false
    void (async () => {
      try {
        // Room token when we have one — it is what a guest holds, and the
        // authed endpoint 401s for them, leaving them with an empty panel until
        // the host happened to act. Falls back to the authed route for a
        // signed-in user who is not yet in a room.
        const credentials = useMeetingSessionStore.getState().roomCredentials
        const roomId = credentials?.channelTopic?.replace(/^room:/, '')
        const { polls: existing, myVotes } =
          credentials?.roomToken && roomId
            ? await pollsApi.listByRoom(roomId, credentials.roomToken)
            : await pollsApi.list(meetingId)
        if (cancelled) return
        const store = useMeetingPollsStore.getState()
        store.setPolls(existing as Poll[])
        // `pollVotes` otherwise only learns of a vote cast in this tab, so after
        // a reload an answered poll offered Vote again and took a second vote.
        store.addOwnVotes(
          (myVotes ?? []).map((v) => ({ ...v, participantName: '', timestamp: 0 })),
        )
      } catch {
        // Non-fatal: a missed fetch still resolves on the next broadcast, and
        // an empty panel beats blocking the meeting on a poll lookup.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [meetingId])

  // Live updates. Every event carries the whole poll with tallies folded in,
  // so one handler serves created, updated and ended alike.
  useEffect(() => {
    if (!channel) return
    const upsert = (payload: { poll?: Poll }) => {
      if (payload?.poll) useMeetingPollsStore.getState().upsertPoll(payload.poll)
    }

    // Only a poll *created* while we are here interrupts anyone. `updated` is a
    // vote landing and `ended` is the host closing it — neither is a question
    // being asked, so neither should raise a dialog over whatever you are doing.
    //
    // This is also what keeps late joiners and reloaders alone: their polls
    // arrive through the catch-up fetch, which never sets this, so they see the
    // poll in the panel and vote when they choose to.
    const created = (payload: { poll?: Poll }) => {
      const poll = payload?.poll
      if (!poll) return
      const store = useMeetingPollsStore.getState()
      store.upsertPoll(poll)
      // Not the author — they wrote the question and vote from the panel.
      if (poll.isActive && !store.authoredPollIds.includes(poll.id)) {
        store.setPromptPollId(poll.id)
      }
    }

    const unsubscribes = [
      channel.subscribe('poll:created', created),
      channel.subscribe('poll:updated', upsert),
      channel.subscribe('poll:ended', upsert),
    ]
    return () => unsubscribes.forEach((off) => off())
  }, [channel])

  return {
    polls,
    activePollId,
    activePoll,
    pollVotes,
    authoredPollIds,
    promptPollId,
    setActivePollId,
    handleCreatePoll,
    handleVotePoll,
    handleEndPoll,
  }
}
